import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import { ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { View } from "@com.mgmtp.a12.client/client-core/lib/core/view";
import { Button } from "@com.mgmtp.a12.widgets/widgets-core/lib/button/main/button.view";
import { ButtonGroup } from "@com.mgmtp.a12.widgets/widgets-core/lib/button-group/main/button-group.view";
import { HTMLInputAdapter } from "@com.mgmtp.a12.widgets/widgets-core/lib/input/buffered";
import { TextLineStateless } from "@com.mgmtp.a12.widgets/widgets-core/lib/input/text-line";
import { ModalNotification } from "@com.mgmtp.a12.widgets/widgets-core/lib/modal-notification/main/modal-notification.view";
import { BaseColumnType, Table } from "@com.mgmtp.a12.widgets/widgets-core/lib/table/new-api";
import { Typography } from "@com.mgmtp.a12.widgets/widgets-core/lib/typography";

import { getViewHtmlId } from "../../focusHandling/sagas";
import { OpenRegisterRecordPreviewRow } from "../../../utilities/rpcHelper";

import { CONFIRM_SELECT_OPENREGISTER_RECORD, SEARCH_OPENREGISTER_RECORDS } from "../actions";
import { OpenregisterSearchModalDescriptor } from "../descriptors";
import { isOpenRegisterSearchModalData, OpenRegisterSearchModalData } from "../types";

const TextLine = HTMLInputAdapter(TextLineStateless);

export const OpenregisterSearchModal: View.ViewComponent = function OpenregisterSearchModal(props) {
	const { activityId } = props;

	const dispatch = useDispatch();
	const descriptor = useSelector(ActivitySelectors.activityPropById(activityId, it => it.descriptor));
	const rawData = useSelector(ActivitySelectors.data(activityId));

	const [query, setQuery] = React.useState("");
	const [pageIndex, setPageIndex] = React.useState(0);
	const [selectedRecordId, setSelectedRecordId] = React.useState<string | undefined>(undefined);

	const isValidDescriptor = OpenregisterSearchModalDescriptor.isInstance(descriptor);
	const modalData: OpenRegisterSearchModalData | undefined = isOpenRegisterSearchModalData(rawData)
		? (rawData as OpenRegisterSearchModalData)
		: undefined;
	const page = modalData?.page;

	React.useEffect(() => {
		if (isValidDescriptor) {
			dispatch(SEARCH_OPENREGISTER_RECORDS({ activityId, pageIndex: 0, query: "" }));
		}
		// Only trigger the initial search once when the modal is opened.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isValidDescriptor, activityId]);

	const onSearch = React.useCallback(() => {
		setPageIndex(0);
		setSelectedRecordId(undefined);
		dispatch(SEARCH_OPENREGISTER_RECORDS({ activityId, pageIndex: 0, query }));
	}, [dispatch, activityId, query]);

	const goToPage = React.useCallback(
		(nextPageIndex: number) => {
			setPageIndex(nextPageIndex);
			setSelectedRecordId(undefined);
			dispatch(SEARCH_OPENREGISTER_RECORDS({ activityId, pageIndex: nextPageIndex, query }));
		},
		[dispatch, activityId, query]
	);

	const onCancel = React.useCallback(() => {
		dispatch(ActivityActions.cancel({ activityId }));
	}, [dispatch, activityId]);

	const onConfirm = React.useCallback(() => {
		if (selectedRecordId) {
			dispatch(CONFIRM_SELECT_OPENREGISTER_RECORD({ activityId, recordId: selectedRecordId }));
		}
	}, [dispatch, activityId, selectedRecordId]);

	const columns = React.useMemo<BaseColumnType<OpenRegisterRecordPreviewRow>[]>(() => {
		const columnNames = new Set<string>();
		page?.items.forEach(row => row.fields.forEach(field => columnNames.add(field.label)));
		return [
			{
				label: "",
				hiddenText: "Datensatz auswählen",
				fixedWidth: true,
				width: 0.1,
				dataGetter: ({ row }) => (
					<input
						type="radio"
						name={`${activityId}-record-selection`}
						aria-label="Datensatz auswählen"
						checked={row.recordId === selectedRecordId}
						onChange={() => setSelectedRecordId(row.recordId)}
					/>
				)
			},
			...Array.from(columnNames).map(column => ({
				label: column,
				dataGetter: ({ row }: { row: OpenRegisterRecordPreviewRow }) =>
					formatCellValue(row.fields.find(field => field.label === column)?.value)
			}))
		];
	}, [activityId, page, selectedRecordId]);

	return (
		<ModalNotification
			id={getViewHtmlId(activityId)}
			onClose={onCancel}
			title="Datensatz aus dem Register auswählen"
			footer={
				<ButtonGroup alignment="right">
					<Button label="Abbrechen" secondary onClick={onCancel} />
					<Button label="Übernehmen" primary disabled={!selectedRecordId} onClick={onConfirm} />
				</ButtonGroup>
			}
		>
			<div className="openregister-search-modal">
				<QueryControls>
					<TextLine
						id={getViewHtmlId(activityId) + "-query-input"}
						label="Suche"
						value={query}
						onValueChange={setQuery}
					/>
					<Button label="Suchen" onClick={onSearch} />
				</QueryControls>

				{modalData?.loading && <p>Lädt…</p>}
				{modalData?.error && <p>Die Suche konnte nicht ausgeführt werden.</p>}

				<Results>
					<Typography.Headline level={2}>Ergebnisse</Typography.Headline>

					{page && !modalData?.loading && (
						<>
							<Table<OpenRegisterRecordPreviewRow>
								data={page.items}
								columns={columns}
								rowKey="recordId"
								rowEventHandlers={({ row }) => ({
									onClick: () => setSelectedRecordId(row.recordId)
								})}
								rowStyling={({ row }) => ({
									interactive: true,
									selected: row.recordId === selectedRecordId
								})}
							/>

							{page.items.length === 0 && <p>Keine Datensätze gefunden.</p>}

							<div
								className="openregister-search-modal__pagination"
								style={{ alignItems: "center", display: "flex", gap: "16px" }}
							>
								<Button
									label="Zurück"
									secondary
									disabled={pageIndex <= 0}
									onClick={() => goToPage(pageIndex - 1)}
								/>
								<span>Seite {pageIndex + 1}</span>
								<Button
									label="Weiter"
									secondary
									disabled={!page.hasNext}
									onClick={() => goToPage(pageIndex + 1)}
								/>
							</div>
						</>
					)}
				</Results>
			</div>
		</ModalNotification>
	);
};

const QueryControls = styled.div`
	display: flex;
	align-items: flex-end;
	gap: 8px;
`;

const Results = styled.section`
	margin-top: 24px;
`;

function formatCellValue(value: unknown): string {
	if (value === null || value === undefined) {
		return "";
	}
	if (typeof value === "object") {
		return JSON.stringify(value);
	}
	return String(value);
}
